import moduleInfo from '../module.json';
import type { AppRuntime } from '@ayocore/platform/client/runtime/AppRuntime';
import { IconMessage } from '@tabler/icons-react';
import { lazy } from 'react';
import { NoPaddingWhiteContainerLayout } from '@ayocore/platform/client/layouts/container-layouts';
import {
  ACTIVITY_DM_ENTITY_TYPE,
  ACTIVITY_TASK_THREAD_ENTITY_TYPE,
} from '../common/activity/constants';
import { registerActivityEntity } from './extensions';
import { ActivityBadgeController } from './components/Activity/controllers/ActivityBadgeController';
import { ActivityDockBadge } from './components/Activity/ui/ActivityDockBadge';

/**
 * Бутстрап модуля Activity (клиент).
 *
 * Регистрирует глобальный док-мессенджер и полноэкранную страницу Activity.
 * Модули-владельцы сущностей подключают свои дескрипторы/команды/провайдеры
 * через реестры `@ayocore/activity/client/extensions` из собственных bootstrap.
 */
export async function setup(app: AppRuntime) {
  const activity = app.useModule(moduleInfo.name, {
    version: moduleInfo.version,
    description: moduleInfo.description,
  });

  // DM-диалоги: built-in тип треда (лейбл ленты — имя собеседника,
  // резолвится сервером per-viewer; карточки/scope у диалога нет).
  registerActivityEntity({
    entityType: ACTIVITY_DM_ENTITY_TYPE,
    label: 'Диалог',
  });

  // Треды задач (проекты): лейбл ленты — title задачи (декорируется сервером).
  // Scope-лейблы задач самодостаточны («Задача #5») — префикс типа не нужен.
  registerActivityEntity({
    entityType: ACTIVITY_TASK_THREAD_ENTITY_TYPE,
    label: 'Проект',
    formatScopeBadge: scopeLabel => scopeLabel,
    formatScopeTitle: scopeLabel => scopeLabel ?? 'Задача',
  });

  // Инициализируем синглтон бейджа: подписка на WS и счётчики стартует
  // сразу, чтобы бейдж был актуален даже до открытия Activity-панели.
  ActivityBadgeController.getInstance();

  activity.useDock({
    id: 'crm-activity',
    label: 'Сообщения',
    icon: IconMessage,
    component: lazy(() =>
      import('./components/Activity/ui/ActivityDockPanel').then(m => ({
        default: m.ActivityDockPanel,
      }))
    ),
    badge: ActivityDockBadge,
    order: 20,
    mode: 'fullscreen',
  });

  activity.useNavigation([
    {
      icon: IconMessage,
      label: 'Сообщения',
      // Исторический путь сохранён при выносе Activity из CRM —
      // не ломаем закладки и привычки пользователей.
      url: '/crm/activity',
      component: lazy(() => import('./components/Activity/ui/ActivityList')),
      layout: NoPaddingWhiteContainerLayout,
      hiddenFromMenu: true,
    },
  ]);
}
